import _ from 'lodash';

export function buildPathOfPermissions(permissions: any[]) {
  return _.map(permissions, (permission) => {
    const path = permission.groups.join('/');
    const permissionId = permission.id;
    return `${path}:${permissionId}`;
  });
}

export function reducePathOfPermissions(permissions: string[]) {
  return _.reduce(permissions, (accu: string[], permission: string) => {
    if (accu.length === 0) {
      accu.push(permission);
    } else {
      const index = _.findIndex(accu, (prec: string) => prec.split(':')[0] === permission.split(':')[0]);
      if (index === -1) {
        accu.push(permission);
      } else {
        accu[index] = `${accu[index]}&${permission.split(':')[1]}`;
      }
    }
    return accu;
  }, []);
}

function createLeaf(name: string) {
  let tmpId = '';
  if (name.includes('&')) {
    tmpId = name.split('&')[0];
  } else {
    tmpId = name;
  }
  const realIdSize = tmpId.length - (tmpId.includes('.read') ? 5 : 6);
  const realId = tmpId.substring(0, realIdSize);
  return {
    id: realId,
    read: name.includes('read') ? false : undefined,
    write: name.includes('write') ? false : undefined
  };
}

function createNode(name: string | undefined, children: any[] = [], id = '') {
  return {
    id: id === '' ? name : id,
    name,
    children,
    data: undefined
  };
}

function insertNode(tree: any, key: string | undefined, node: any) {
  return _.map(tree, (element: any) => {
    if (element.id === key) {
      element.children.push(node);
      element.children = _.uniqBy(element.children, 'name');
    } else {
      element.children = insertNode(element.children, key, node);
    }
    return element;
  });
}

function insertLeaf(tree: any, key: string | undefined, leaf: any) {
  return _.map(tree, (element: any) => {
    if (element.id === key) {
      if (element.data === undefined) {
        element.data = leaf;
      } else {
        element.data.read = leaf.read === undefined ? element.data.read : leaf.read;
        element.data.write = leaf.write === undefined ? element.data.write : leaf.write;
      }
    } else {
      element.children = insertLeaf(element.children, key, leaf);
    }
    return element;
  });
}

export function reduceTreeOfPermissions(permissions: string[]) {
  return _.reduce(permissions, (accu: any[], permission: string) => {
    const exploded = permission.split('/');
    const root = exploded.length >= 1 ? exploded.shift() : '';
    if (accu.length === 0) {
      // First node
      const node = createNode(root);
      let tree = [node];
      _.forEach(exploded, (element) => {
        const elementData = element.split(':');
        const isALeaf = elementData.length === 2;

        if (isALeaf) {
          const intermediate = createNode(elementData[0]);
          tree = insertNode(tree, root, intermediate);
          tree = insertLeaf(tree, intermediate.name, createLeaf(elementData[1]));
        } else {
          tree = insertNode(tree, root, createNode(elementData[0]));
        }
      });
      accu.push(tree[0]);
    } else {
      const nodeIndex = _.findIndex(accu, (current: any) => current.name === root);
      if (nodeIndex === -1) {
        // Add to root if root !exist

        if (root && root.includes(':')) {
          const node: any = createNode(root.split(':')[0]);
          const leaf = createLeaf(root.split(':')[1]);
          node.data = leaf;
          accu.push(node);
        } else {
          const node = createNode(root);
          if (exploded.length !== 0) {
            _.each(exploded, (element) => {
              const intermediate: any = createNode(element.split(':')[0]);
              const leaf = createLeaf(element.split(':')[1]);
              intermediate.data = leaf;
              node.children.push(intermediate);
            });
          }
          accu.push(node);
        }
      } else {
        let tree = [accu[nodeIndex]];
        let parent = accu[nodeIndex].name;
        _.forEach(exploded, (element) => {
          const elementData = element.split(':');
          if (elementData.length === 1) {
            const intermediate = createNode(elementData[0]);
            tree = insertNode(tree, parent, intermediate);
            parent = intermediate.name;
          } else {
            const intermediate = createNode(elementData[0]);
            tree = insertNode(tree, parent, intermediate);
            parent = intermediate.name;
            tree = insertLeaf(tree, intermediate.name, createLeaf(elementData[1]));
          }
        });
      }
    }
    return accu;
  }, []);
}

export function updateTree(tree: any, permission: string, value = true) {
  const permissionIdSize = permission.length - (permission.includes('read') ? 5 : 6);
  const realPermissionId = permission.substring(0, permissionIdSize);
  const right = permission.includes('read') ? 'read' : 'write';
  return _.map(tree, (node) => {
    if (node.data) {
      if (node.data.id === realPermissionId) {
        node.data[right] = value;
      }
    } else {
      node.children = updateTree(node.children, permission, value);
    }
    return node;
  });
}

export function permissionsListOfTree(tree: any[]) {
  return _.reduce(tree, (accu: any[], elem: any) => {
    if (elem.data !== undefined) {
      const id: string = elem.data ? elem.data.id : '';
      if (elem.data.read) {
        accu.push(`${id}.read`);
      }
      if (elem.data.write) {
        accu.push(`${id}.write`);
      }
    } else {
      accu = accu.concat(permissionsListOfTree(elem.children));
    }
    return _.uniq(accu);
  }, []);
}

export function checkRightsAllChildren(tree: any, permission: string, right: string, all: boolean) {
  return _.uniq(_.reduce(tree, (accu: any[], node: any) => {
    if (node.name === permission && !all) {
      accu = accu.concat(checkRightsAllChildren(node.children, permission, right, true));
    } else if (all) {
      if (node.data) {
        if (node.data[right] !== undefined) {
          accu.push(node.data[right]);
        } else if (node.children) {
          accu = accu.concat(checkRightsAllChildren(node.children, permission, right, all));
        }
      } else if (node.children) {
        const sub = checkRightsAllChildren(node.children, permission, right, true);
        accu.push(sub);
        accu = _.flatten(accu);
      }
    } else {
      accu = accu.concat(checkRightsAllChildren(node.children, permission, right, false));
    }
    return accu;
  }, []));
}

export function updateAllChildren(tree: any, permission: string, right: string, value: boolean, all: boolean) {
  return _.map(tree, (node) => {
    if (node.name === permission && !all) {
      node.chilren = updateAllChildren(node.children, permission, right, value, true);
    } else if (all) {
      if (node.data && node.data[right] !== undefined) {
        if (((node.name !== 'User Role Management') && (node.name !== 'Master Management')) || (right !== 'read')) {
          node.data[right] = value;
        }
      } else {
        node.children = updateAllChildren(node.children, permission, right, value, all);
      }
    } else {
      node.children = updateAllChildren(node.children, permission, right, value, all);
    }
    return node;
  });
}
